import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Modal, Row, Select, Upload } from "antd";
import { getMediaUrl } from "config/env";
import { flattenCategoriesTree, getBase64, getCategoryDetailFromResponse, getCategoryListFromResponse } from "functions/functions";
import Loading from "layouts/Loading/Loading";
import OpenNotificationWithIcon from "layouts/notificationWithIcon/OpenNotificationWithIcon ";
import Axios from "middleware/axiosInstance";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const { Option } = Select;

const AddAndEditCategory = () => {
  const { categoryId } = useParams();
  const isInEditMode = Boolean(categoryId);
  const navigate = useNavigate();

  const [allCategories, setAllCategories] = useState([]);
  const [categoryInfoForEdit, setCategoryInfoForEdit] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [fileList, setFileList] = useState([]);

  const handleCancel = () => setPreviewOpen(false);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url.substring(file.url.lastIndexOf("/") + 1)
    );
  };

  const handleChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const getAllCategories = useCallback(() => {
    Axios.get("/category")
      .then((res) => {
        const rootCategories = getCategoryListFromResponse(res.data);
        setAllCategories(flattenCategoriesTree(rootCategories));
      })
      .catch((err) => console.log(err));
  }, []);

  const getCategoryInfoForEdit = useCallback(() => {
    Axios.get(`/category/${categoryId}`)
      .then((res) => {
        const category = getCategoryDetailFromResponse(res.data);
        setCategoryInfoForEdit(category);
        if (category?.image) {
          setFileList([
            {
              thumbUrl: getMediaUrl(category.image),
              preview: getMediaUrl(category.image),
            },
          ]);
        }
      })
      .catch((err) => console.log(err));
  }, [categoryId]);

  useEffect(() => {
    getAllCategories();
    if (isInEditMode) {
      getCategoryInfoForEdit();
    }
  }, [getAllCategories, getCategoryInfoForEdit, isInEditMode]);

  const submitNewCategory = async (category) => {
    if (category.parent_id === undefined) {
      category.parent_id = null;
    }

    if (fileList[0]) {
      category.image = await getBase64(fileList[0].originFileObj);
    }

    Axios.post("/category", category)
      .then(() => {
        navigate("/categories");
        OpenNotificationWithIcon("ثبت موفق", "با موفقیت افزوده شد");
      })
      .catch((err) => console.log(err));
  };

  const categoryEditHandler = async (category) => {
    if (category.parent_id === undefined) {
      category.parent_id = null;
    }

    if (fileList[0]) {
      if (fileList[0].originFileObj) {
        category.image = await getBase64(fileList[0].originFileObj);
      } else {
        delete category.image;
      }
    }

    Axios.put(`/category/${categoryId}`, category)
      .then(() => {
        navigate("/categories");
        OpenNotificationWithIcon("ویرایش موفق", "با موفقیت ویرایش شد");
      })
      .catch((err) => console.log(err));
  };

  const descendantsOfEditingCategory = useMemo(() => {
    if (!isInEditMode || !allCategories.length) return new Set();

    const childrenByParent = allCategories.reduce((acc, item) => {
      const parentKey = item.parent_id;
      if (!acc[parentKey]) acc[parentKey] = [];
      acc[parentKey].push(item.id);
      return acc;
    }, {});

    const descendants = new Set();
    const queue = [Number(categoryId)];

    while (queue.length > 0) {
      const current = queue.shift();
      const children = childrenByParent[current] || [];

      children.forEach((childId) => {
        if (!descendants.has(childId)) {
          descendants.add(childId);
          queue.push(childId);
        }
      });
    }

    return descendants;
  }, [allCategories, isInEditMode, categoryId]);

  const parentCategoryOptions = useMemo(() => {
    if (!isInEditMode) return allCategories;

    const editingCategoryId = Number(categoryId);

    return allCategories.filter(
      (category) =>
        category.id !== editingCategoryId &&
        !descendantsOfEditingCategory.has(category.id)
    );
  }, [allCategories, descendantsOfEditingCategory, isInEditMode, categoryId]);

  return (
    <div className="customized-container">
      <div className="customized-wrapper">
        <h5 className="title-session">
          {isInEditMode ? "ویرایش دسته غذایی" : "افزودن دسته غذایی"}
        </h5>

        {isInEditMode && categoryInfoForEdit === null ? (
          <Loading />
        ) : (
          <Form
            layout="vertical"
            initialValues={categoryInfoForEdit}
            autoComplete="off"
            scrollToFirstError
            labelCol={{ xs: 24 }}
            onFinish={isInEditMode ? categoryEditHandler : submitNewCategory}
          >
            <Row gutter={8}>
              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item label="دسته والد (اختیاری)" name="parent_id">
                  <Select
                    allowClear
                    placeholder="بدون والد (دسته اصلی)"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {parentCategoryOptions.map((category) => (
                      <Option key={category.id} value={category.id}>
                        {category.title_fa}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item label="عنوان فارسی" name="title_fa">
                  <Input placeholder="پیتزا" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item label="نام انگلیسی" name="title_en">
                  <Input placeholder="Pizza" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item label="نام عربی" name="title_ar">
                  <Input placeholder="البيتزا" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} xl={6}>
                <Form.Item name="image" label="تصویر">
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    onPreview={handlePreview}
                    onChange={handleChange}
                    beforeUpload={() => false}
                    maxCount={1}
                  >
                    {fileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>

                <Modal
                  open={previewOpen}
                  title={previewTitle}
                  footer={null}
                  onCancel={handleCancel}
                >
                  <img alt="example" style={{ width: "100%" }} src={previewImage} />
                </Modal>
              </Col>

              <Button htmlType="submit" type="primary">
                {isInEditMode ? "ثبت تغییرات" : "افزودن"}
              </Button>
            </Row>
          </Form>
        )}
      </div>
    </div>
  );
};

export default AddAndEditCategory;
